/**
 * @file: useOcrResultServiceInfoFeature.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { OrderExtra } from '@euler/app/flows/order/types';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { ServiceSubjectType } from '@euler/model/enum';
import { PendingIssue } from '@euler/model/report';
import { VehicleServiceInfo } from '@euler/services';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { useCallback, useMemo } from 'react';
import { BehaviorSubject } from 'rxjs';

export const useOcrResultServiceInfoFeature = (
  orderDetail$: BehaviorSubject<OrderExtra>,
  serviceInfo: VehicleServiceInfo | null | undefined,
) => {
  const confirm = useConfirm();
  const navigation = useAppNavigation();
  const [orderDetail] = useBehaviorSubject(orderDetail$);
  const addedPendingIssues = useMemo<string[] | undefined>(
    () =>
      orderDetail.serviceSubjects
        ?.filter(
          x =>
            x.type === ServiceSubjectType.Construction &&
            x.pendingIssueId != null,
        )
        .map(x => (x as any).pendingIssueId),
    [orderDetail.serviceSubjects],
  );

  const onTogglePendingIssue = usePersistFn((pendingIssue: PendingIssue) => {
    if (
      orderDetail.serviceSubjects?.some(
        x =>
          x.type === ServiceSubjectType.Construction &&
          x.pendingIssueId === pendingIssue.issueId,
      )
    ) {
      void confirm({
        title: '是否删除该遗留问题对应的施工项目?',
      }).then(res => {
        if (res) {
          orderDetail$.next({
            ...orderDetail$.value,
            serviceSubjects: orderDetail$.value.serviceSubjects?.filter(
              x =>
                x.type !== ServiceSubjectType.Construction ||
                x.pendingIssueId !== pendingIssue.issueId,
            ),
          });
        }
      });
    } else {
      navigation.push('_selectMember', {
        title: '项目派工',
        onSelect: member => {
          navigation.goBack();
          orderDetail$.next({
            ...orderDetail$.value,
            serviceSubjects: [
              ...(orderDetail$.value.serviceSubjects ?? []),
              {
                type: ServiceSubjectType.Construction,
                name: pendingIssue.name,
                pendingIssueId: pendingIssue.issueId,
                assignedTo: {
                  id: member.id,
                  name: member.name,
                },
              },
            ],
          });
        },
      });
    }
  });

  const onViewPendingIssues = useCallback(
    (serviceRecordId: string) => {
      navigation.push('_pendingIssues', {
        pendingIssues:
          serviceInfo?.serviceRecords.find(x => x._id === serviceRecordId)
            ?.pendingIssuesV2 ?? [],
      });
    },
    [navigation, serviceInfo?.serviceRecords],
  );

  return { addedPendingIssues, onViewPendingIssues, onTogglePendingIssue };
};
